import { useState, useMemo, useEffect, useRef } from 'react';
import { HelpCircle, ArrowRight, Wine, Hotel, Utensils, Sandwich, Coffee, Award, Heart, Sparkles, MapPin } from 'lucide-react';

/**
 * AiSearchClarification — token-saving qualifier UI.
 *
 * When the backend `query_qualifier` deflects an ambiguous query
 * ("picnic deli", "wheelchair accessible") we render this card
 * instead of an itinerary. The user picks a category chip + a region
 * chip (or just a category if only one region was offered) and the
 * parent hook fires `resolveClarification(pick, region)` which
 * expands the query and re-submits.
 *
 * Why a two-stage chip selector instead of one combined button:
 *   - The pick × region combinatorics would create 9-12 buttons,
 *     which is choice paralysis on mobile.
 *   - Selecting them sequentially mirrors the way a human concierge
 *     would actually ask ("are you looking for wineries or
 *     restaurants? — and in Napa or Sonoma?").
 *   - Region pre-selects to the only choice when the user already
 *     specified one, collapsing the UI to a one-click experience.
 */

// Icon lookup — taxonomy keys icons by lucide-react component name.
// Keeping this here (not in the backend payload) lets us swap icons
// without API contract churn.
const ICONS = {
  Wine,
  Hotel,
  Utensils,
  Sandwich,
  Coffee,
  Award,
  Heart,
  Sparkles,
};

const AiSearchClarification = ({ clarification, onResolve, onCancel }) => {
  const picks = useMemo(() => Array.isArray(clarification?.picks) ? clarification.picks : [], [clarification]);
  const regions = useMemo(() => Array.isArray(clarification?.regions) ? clarification.regions : [], [clarification]);

  // Multi-select state. Users routinely want to plan trips that
  // combine MULTIPLE verticals on a single day — e.g. "Wineries +
  // Hotels + Spas" for a weekend retreat. We keep the API single-pick
  // (one expand template per pick) but compose a joined natural-language
  // query when the user selects 2+ picks. See `submit()` below for the
  // join logic.
  const [selectedPicks, setSelectedPicks] = useState(/** @type {object[]} */ ([]));
  const [selectedRegion, setSelectedRegion] = useState(
    regions.length === 1 ? regions[0] : null,
  );

  const togglePick = (pick) => {
    setSelectedPicks((prev) => {
      const exists = prev.find((p) => p.label === pick.label);
      return exists ? prev.filter((p) => p.label !== pick.label) : [...prev, pick];
    });
  };

  // Mobile: when the clarification card mounts, the viewport often
  // sits on the search bar that triggered it — leaving the card
  // off-screen above the fold and forcing the user to scroll up to
  // see what they need to answer. Auto-scroll the card into view on
  // mount so the question is the first thing visible after submit.
  // Desktop normally has both bar and card visible side-by-side, so
  // the scroll is a no-op there.
  const cardRef = useRef(null);
  useEffect(() => {
    if (!clarification || !cardRef.current) return;
    const t = setTimeout(() => {
      try {
        cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (_e) {
        cardRef.current?.scrollIntoView();
      }
    }, 50);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clarification?.original_query]);

  if (!clarification) return null;

  const ready = Boolean(selectedPicks.length > 0 && selectedRegion);

  const submit = () => {
    if (!ready) return;
    // Single-pick path: respect the carefully-templated `expand` string
    // exactly (preserves any "For someone interested in X:" interest
    // prefix injected by the backend qualifier).
    if (selectedPicks.length === 1) {
      onResolve(selectedPicks[0], selectedRegion);
      return;
    }
    // Multi-pick path: build a joined natural-language query that
    // lists all chosen verticals in one ask. We preserve any interest
    // prefix from the first pick (all picks in a clarification share
    // the same prefix because they were built from the same query).
    const prefixMatch = /^(For someone interested in [^:]+:\s*)/i.exec(
      selectedPicks[0].expand || '',
    );
    const interestPrefix = prefixMatch ? prefixMatch[1] : '';
    // Convert each pick label into a clean noun phrase ("Wineries &
    // tastings" → "wineries and tastings"). The label is the user-
    // facing copy so it reads naturally when joined.
    const cats = selectedPicks.map((p) =>
      (p.label || '').toLowerCase().replace(/ & /g, ' and ').replace(/&/g, 'and').trim(),
    );
    // Oxford-comma join: "X", "X and Y", "X, Y, and Z"
    let joined;
    if (cats.length === 2) joined = `${cats[0]} and ${cats[1]}`;
    else joined = `${cats.slice(0, -1).join(', ')}, and ${cats[cats.length - 1]}`;

    const composedExpand = `${interestPrefix}best ${joined} in {region}`;
    // Synthesise a pick-like object so the parent's resolver can run
    // its existing `.replace('{region}', value)` step without
    // branching.
    const composedPick = {
      label: selectedPicks.map((p) => p.label).join(' + '),
      expand: composedExpand,
      icon: selectedPicks[0].icon,
    };
    onResolve(composedPick, selectedRegion);
  };

  return (
    <div
      ref={cardRef}
      className="rounded-2xl border-2 border-wine-200 bg-gradient-to-br from-wine-50 via-white to-gold-50/30 p-4 sm:p-5 shadow-sm scroll-mt-20"
      data-testid="ai-search-clarification"
    >
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-wine-600 to-wine-800 flex items-center justify-center shadow-md">
          <HelpCircle className="w-4.5 h-4.5 text-gold-300" strokeWidth={2.5} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-wine-700 mb-1">
            Quick question
          </div>
          {/* Headline contains pre-escaped HTML entities from the backend
              (e.g. &ldquo;...&rdquo;). Render via dangerouslySetInnerHTML
              because we control the source and want proper typographic quotes. */}
          <p
            className="text-[13px] sm:text-sm text-[#1a1a2e] leading-snug"
            dangerouslySetInnerHTML={{ __html: clarification.headline }}
            data-testid="ai-clarification-headline"
          />
        </div>
      </div>

      {/* Step 1: Category picks */}
      <div className="mt-4">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500 mb-2">
          1. What kind of stops? <span className="text-gray-400 normal-case tracking-normal">(pick one or more)</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="ai-clarification-picks">
          {picks.map((pick, i) => {
            const Icon = ICONS[pick.icon] || Sparkles;
            const isSelected = Boolean(selectedPicks.find((p) => p.label === pick.label));
            return (
              <button
                key={pick.label}
                type="button"
                onClick={() => togglePick(pick)}
                className={
                  'flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-[12.5px] font-medium transition-all border-2 ' +
                  (isSelected
                    ? 'bg-wine-700 text-white border-wine-700 shadow-md'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-wine-400 hover:bg-wine-50/40')
                }
                data-testid={`ai-clarification-pick-${i}`}
                aria-pressed={isSelected}
              >
                <Icon className={'w-4 h-4 shrink-0 ' + (isSelected ? 'text-gold-300' : 'text-wine-700')} />
                <span className="truncate flex-1">{pick.label}</span>
                {isSelected && (
                  <span className="text-[10px] font-bold text-gold-300 shrink-0">✓</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Step 2: Region picks (skip rendering if pre-resolved to a single region) */}
      {regions.length > 1 && (
        <div className="mt-3.5">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500 mb-2">
            2. Which region?
          </div>
          <div className="flex flex-wrap gap-2" data-testid="ai-clarification-regions">
            {regions.map((region, i) => {
              const isSelected = selectedRegion?.value === region.value;
              return (
                <button
                  key={region.value}
                  type="button"
                  onClick={() => setSelectedRegion(region)}
                  className={
                    'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold transition-all border-2 ' +
                    (isSelected
                      ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-emerald-400 hover:bg-emerald-50/40')
                  }
                  data-testid={`ai-clarification-region-${i}`}
                  aria-pressed={isSelected}
                >
                  <MapPin className="w-3 h-3" />
                  {region.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* CTA bar */}
      <div className="mt-4 flex items-center justify-between gap-3 pt-3 border-t border-wine-100">
        <button
          type="button"
          onClick={onCancel}
          className="text-[12px] text-gray-500 hover:text-gray-700 transition-colors underline-offset-2 hover:underline"
          data-testid="ai-clarification-cancel"
        >
          Not quite — let me rephrase
        </button>
        <div className="flex items-center gap-2">
          {selectedPicks.length > 0 && (
            <span className="text-[10.5px] text-gray-500" data-testid="ai-clarification-pick-count">
              {selectedPicks.length} selected
            </span>
          )}
          <button
            type="button"
            disabled={!ready}
            onClick={submit}
            className={
              'inline-flex items-center gap-2 rounded-full px-4 py-2 text-[12.5px] font-bold transition-all ' +
              (ready
                ? 'bg-wine-700 text-white hover:bg-wine-800 shadow-md hover:shadow-lg'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed')
            }
            data-testid="ai-clarification-submit"
          >
            Plan it
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AiSearchClarification;
